import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FileText, Loader2, Menu, Plus, Trash2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { SheetTrigger } from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { EmptyState } from '@/components/empty-state'
import { cn } from '@/lib/utils'
import {
  useContextDocuments,
  useCreateContextDocument,
  useDeleteContextDocument,
  useUpdateContextDocument,
  useUploadContextDocument,
} from '@/features/settings/use-context'
import type { ContextDocument } from '@/features/settings/context-api'

const DOC_TYPES = [
  { value: 'general', label: 'Allgemein' },
  { value: 'company', label: 'Unternehmen' },
  { value: 'user', label: 'Persönlich' },
  { value: 'project', label: 'Projekt' },
] as const

export function SettingsContextPage() {
  const docsQuery = useContextDocuments()
  const upload = useUploadContextDocument()
  const [activeId, setActiveId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const docs = docsQuery.data ?? []
  const active = docs.find((d) => d.id === activeId) ?? docs[0] ?? null

  useEffect(() => {
    if (!activeId && docs[0]) setActiveId(docs[0].id)
  }, [docs, activeId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/markdown': ['.md', '.markdown'],
      'text/plain': ['.txt'],
    },
    maxFiles: 5,
    onDrop: async (files) => {
      for (const file of files) {
        try {
          await upload.mutateAsync({ file })
          toast.success(`${file.name} hochgeladen`)
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Upload fehlgeschlagen'
          toast.error(`${file.name}: ${msg}`)
        }
      }
    },
  })

  return (
    <div className="flex h-full">
      {/* Desktop sidebar */}
      <aside className="hidden w-72 border-r border-border bg-muted/20 lg:flex lg:flex-col">
        <ContextDocNav
          docs={docs}
          isLoading={docsQuery.isLoading}
          activeId={active?.id ?? null}
          isUploading={upload.isPending}
          isDragActive={isDragActive}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          onSelect={setActiveId}
          onCreate={() => setCreateOpen(true)}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile Nav-Bar (M8.4) — spiegelt das App-Shell-Pattern */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 lg:hidden">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="-ml-1 inline-flex h-11 w-11 min-[761px]:h-9 min-[761px]:w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Kontext-Navigation öffnen"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Kontext-Navigation</SheetTitle>
              </SheetHeader>
              <ContextDocNav
                docs={docs}
                isLoading={docsQuery.isLoading}
                activeId={active?.id ?? null}
                isUploading={upload.isPending}
                isDragActive={isDragActive}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                onSelect={setActiveId}
                onCreate={() => setCreateOpen(true)}
                onNavigate={() => setMobileNavOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <span className="text-sm font-semibold">Kontext-Dokumente</span>
        </div>

        <main className="flex-1 overflow-y-auto px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
          {active ? (
            <ContextEditor key={active.id} doc={active} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <EmptyState
                icon={FileText}
                title="Kein Dokument ausgewählt"
                description="Datei in der Seitenleiste ablegen oder ein neues Kontext-Dokument anlegen."
                className="max-w-md"
              />
            </div>
          )}
        </main>
      </div>

      <CreateContextDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => setActiveId(id)} />
    </div>
  )
}

type DropzoneState = ReturnType<typeof useDropzone>

interface ContextDocNavProps {
  docs: ContextDocument[]
  isLoading: boolean
  activeId: string | null
  isUploading: boolean
  isDragActive: boolean
  getRootProps: DropzoneState['getRootProps']
  getInputProps: DropzoneState['getInputProps']
  onSelect: (id: string) => void
  onCreate: () => void
  onNavigate?: () => void
}

function ContextDocNav({
  docs,
  isLoading,
  activeId,
  isUploading,
  isDragActive,
  getRootProps,
  getInputProps,
  onSelect,
  onCreate,
  onNavigate,
}: ContextDocNavProps) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Kontext-Dokumente</h2>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onCreate()
            onNavigate?.()
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="p-2">
        <div
          {...getRootProps()}
          className={cn(
            'mb-2 flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border px-3 py-4 text-center text-xs hover:bg-muted',
            isDragActive && 'border-accent bg-muted',
          )}
        >
          <input {...getInputProps()} />
          <Upload className="h-5 w-5 text-muted-foreground" />
          {isUploading ? (
            <span className="text-muted-foreground">Hochladen…</span>
          ) : isDragActive ? (
            <span className="text-foreground">Loslassen zum Hochladen</span>
          ) : (
            <span className="text-muted-foreground">Datei droppen oder klicken (PDF/DOCX/MD)</span>
          )}
        </div>
        <ul className="space-y-1">
          {isLoading ? (
            <li className="px-2 py-3 text-xs text-muted-foreground">Lädt…</li>
          ) : docs.length === 0 ? (
            <li className="px-2 py-3 text-xs text-muted-foreground">Keine Dokumente.</li>
          ) : (
            docs.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(d.id)
                    onNavigate?.()
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
                    activeId === d.id && 'bg-muted font-medium',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="break-words">{d.title}</span>
                  </span>
                  {d.is_active ? (
                    <Badge variant="outline" className="ml-2 shrink-0">
                      aktiv
                    </Badge>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </>
  )
}

function ContextEditor({ doc }: { doc: ContextDocument }) {
  const updateMutation = useUpdateContextDocument()
  const deleteMutation = useDeleteContextDocument()
  const [form, setForm] = useState({
    title: doc.title,
    description: doc.description ?? '',
    document_type: doc.document_type,
    extracted_text: doc.extracted_text,
    is_active: doc.is_active,
  })

  useEffect(() => {
    setForm({
      title: doc.title,
      description: doc.description ?? '',
      document_type: doc.document_type,
      extracted_text: doc.extracted_text,
      is_active: doc.is_active,
    })
  }, [doc.id, doc.title, doc.description, doc.document_type, doc.extracted_text, doc.is_active])

  const save = async () => {
    try {
      await updateMutation.mutateAsync({
        id: doc.id,
        input: {
          title: form.title,
          description: form.description,
          document_type: form.document_type,
          extracted_text: form.extracted_text,
          is_active: form.is_active,
        },
      })
      toast.success('Gespeichert')
    } catch {
      toast.error('Speichern fehlgeschlagen')
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Dokument "${doc.title}" löschen?`)) return
    try {
      await deleteMutation.mutateAsync(doc.id)
      toast.success('Gelöscht')
    } catch {
      toast.error('Löschen fehlgeschlagen')
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="break-words text-[26px] font-[650] leading-tight">{doc.title}</h1>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => void handleDelete()}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Metadaten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 items-center gap-2 min-[761px]:grid-cols-3 min-[761px]:gap-3">
            <Label className="text-sm text-muted-foreground">Titel</Label>
            <Input
              className="min-[761px]:col-span-2"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 items-center gap-2 min-[761px]:grid-cols-3 min-[761px]:gap-3">
            <Label className="text-sm text-muted-foreground">Typ</Label>
            <div className="min-[761px]:col-span-2">
              <Select
                value={form.document_type}
                onValueChange={(v) => setForm({ ...form, document_type: v as typeof form.document_type })}
              >
                <SelectTrigger aria-label="Dokumenttyp">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 items-start gap-2 min-[761px]:grid-cols-3 min-[761px]:gap-3">
            <Label className="min-[761px]:pt-2 text-sm text-muted-foreground">Beschreibung</Label>
            <Textarea
              className="min-[761px]:col-span-2"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 items-center gap-2 min-[761px]:grid-cols-3 min-[761px]:gap-3">
            <Label className="text-sm text-muted-foreground">Aktiv</Label>
            <div className="min-[761px]:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                className="h-11 w-11 shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-4 min-[761px]:w-4"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <span className="text-sm text-muted-foreground">
                Wenn aktiv, fliesst das Dokument in jeden Chat + Output ein.
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inhalt (extrahiert)</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={20}
            value={form.extracted_text}
            onChange={(e) => setForm({ ...form, extracted_text: e.target.value })}
            className="font-mono text-[13px]"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => void save()} disabled={updateMutation.isPending}>
          {updateMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Speichern
        </Button>
      </div>
    </div>
  )
}

interface CreateContextDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
}

function CreateContextDialog({ open, onOpenChange, onCreated }: CreateContextDialogProps) {
  const create = useCreateContextDocument()
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [type, setType] = useState<'company' | 'user' | 'project' | 'general'>('general')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Neues Kontext-Dokument</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" />
          <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
            <SelectTrigger aria-label="Dokumenttyp">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Inhalt (Markdown)…"
            className="font-mono text-[13px]"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button
            disabled={!title.trim() || !text.trim() || create.isPending}
            onClick={async () => {
              try {
                const created = await create.mutateAsync({
                  title: title.trim(),
                  extracted_text: text.trim(),
                  document_type: type,
                })
                onCreated(created.id)
                toast.success(`Dokument "${created.title}" angelegt`)
                setTitle('')
                setText('')
                setType('general')
                onOpenChange(false)
              } catch {
                toast.error('Anlegen fehlgeschlagen')
              }
            }}
          >
            {create.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
